import * as yup from 'yup';

// Phone number validation regex (Australian format)
const phoneRegex = /^(\+61|0)[2-9]\d{8}$/;

// ABN validation function
const validateABN = (abn) => {
  if (!abn) return true; // Allow empty for optional fields
  const cleanABN = abn.replace(/[^0-9]/g, '');
  if (cleanABN.length !== 11) return false;
  
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  let sum = 0;
  
  for (let i = 0; i < 11; i++) {
    let digit = parseInt(cleanABN[i], 10);
    if (i === 0) digit = digit - 1;
    sum += digit * weights[i];
  }
  return sum % 89 === 0;
};

// Base schema for common fields
const baseSchema = {
  userType: yup
    .string()
    .required('Please select whether you are looking for work or hiring workers')
    .oneOf(['jobseeker', 'company'], 'Please select a valid user type'),
};

// Profile fields (basic user info)
const profileSchema = {
  firstName: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters')
    .matches(/^[a-zA-Z\s'-]+$/, 'First name can only contain letters, spaces, hyphens, and apostrophes'),
  
  lastName: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters')
    .matches(/^[a-zA-Z\s'-]+$/, 'Last name can only contain letters, spaces, hyphens, and apostrophes'),
  
  email: yup
    .string()
    .required('Email address is required')
    .email('Please enter a valid email address')
    .max(100, 'Email must be less than 100 characters'),
  
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(phoneRegex, 'Please enter a valid Australian phone number (e.g., 04XX XXX XXX or +61 4XX XXX XXX)'),
};

// Job seeker specific fields
const jobSeekerSchema = {
  skills: yup
    .array()
    .of(yup.string())
    .min(1, 'Please select at least one skill')
    .required('Skills are required'),
  
  experience: yup
    .string()
    .required('Experience level is required')
    .oneOf(['entry', 'intermediate', 'experienced'], 'Please select a valid experience level'),
  
  availability: yup
    .string()
    .required('Availability is required')
    .oneOf(['immediate', '1week', '2weeks', '1month'], 'Please select your availability'),
  
  expectedSalary: yup
    .string()
    .required('Expected hourly rate is required'),
  
  visaStatus: yup
    .string()
    .required('Visa status is required'),
  
  location: yup
    .string()
    .required('Location/Suburb is required')
    .min(3, 'Please enter a valid Location/Suburb'),
  
  transport: yup
    .string()
    .required('Transport method is required'),
  
  hasValidABN: yup
    .string()
    .required('Please specify if you have a valid ABN')
    .oneOf(['yes', 'no'], 'Please select yes or no'),
};

// Company specific fields
const companySchema = {
  companyName: yup
    .string()
    .required('Company name is required')
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters'),
  
  abn: yup
    .string()
    .required('ABN is required')
    .test('abn-validation', 'Please enter a valid ABN', validateABN),
  
  industry: yup
    .string()
    .required('Industry is required'),
  
  companyPhone: yup
    .string()
    .required('Company phone number is required')
    .matches(phoneRegex, 'Please enter a valid Australian phone number'),
  
  companyEmail: yup
    .string()
    .required('Company email is required')
    .email('Please enter a valid email address')
    .max(100, 'Email must be less than 100 characters'),
  
  preferredCommunication: yup
    .array()
    .of(yup.string())
    .min(1, 'Please select at least one communication channel')
    .required('Preferred communication channels are required'),

};

// Step-specific schemas
export const userTypeStepSchema = yup.object({
  userType: baseSchema.userType,
});

export const basicInfoStepSchema = yup.object({
  profile: yup.object({
    ...profileSchema,
  }),
});

// Job seeker step schema (only validates job seeker specific fields)
export const jobSeekerStepSchema = yup.object({
  jobseeker: yup.object({
    ...jobSeekerSchema,
  }),
});

// Company step schema (only validates company specific fields)
export const companyStepSchema = yup.object({
  company: yup.object({
    ...companySchema,
  }),
});

// Complete schemas for final validation (includes all fields)
export const completeJobSeekerSchema = yup.object({
  ...baseSchema,
  profile: yup.object({
    ...profileSchema,
  }),
  jobseeker: yup.object({
    ...jobSeekerSchema,
  }),
});

export const completeCompanySchema = yup.object({
  ...baseSchema,
  profile: yup.object({
    ...profileSchema,
  }),
  company: yup.object({
    ...companySchema,
  }),
});

// Complete validation schema that adapts based on user type
export const getValidationSchema = (userType) => {
  if (userType === 'jobseeker') {
    return completeJobSeekerSchema;
  } else if (userType === 'company') {
    return completeCompanySchema;
  }
  
  // Default schema for when userType is not selected
  return yup.object({
    ...baseSchema,
    profile: yup.object({
      ...profileSchema,
    }),
  });
};

// Step validation function
export const getStepValidationSchema = (step, userType) => {
  switch (step) {
    case 0:
      return userTypeStepSchema;
    case 1:
      return basicInfoStepSchema;
    case 2:
      if (userType === 'jobseeker') {
        return jobSeekerStepSchema;
      } else if (userType === 'company') {
        return companyStepSchema;
      }
      return basicInfoStepSchema;
    default:
      return userTypeStepSchema;
  }
};